/**
 * LayerCard Component
 *
 * Individual layer card with toggle, opacity, and info controls.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt, faGripLines, faCog } from '@fortawesome/free-solid-svg-icons';
import './LayerCard.scss';
import { Layer } from '../../../types/layer';
import { OpacitySlider } from './OpacitySlider';
import { AffTrackTimeframeControl } from './AffTrackTimeframeControl';
import { AffLiveWindowControl } from './AffLiveWindowControl';
import { LayerIconPreview } from './LayerIconPreview';
import { useLayerDrawer } from '../LayerDrawerContext';
import { usePermissions } from '../../../context/PermissionContext';
import { useTools } from '../../../context/ToolContext';
import { AFF_FEATURE, AFF_READ_OP, AFF_EXPLORE_OP, AFF_SIMULATE_OP } from '../../../shared/affPermissions';
import { useAffSimStatus, AffSimulatorBanner, AffSimulatorControls } from './AffSimulator';
import { LiveLayer } from '../../layers2/LiveLayer';
import AppModal from '../../../components/common/AppModal';

interface LayerCardProps {
  layer: Layer;
  showOpacity?: boolean;
  showInfo?: boolean;
  showDragHandle?: boolean;
  onDragStart?: (layerId: string) => void;
  onDragEnd?: () => void;
}

function formatRefreshInterval(ms: number): string {
  if (ms >= 60000) {
    const mins = Math.round(ms / 60000);
    return `${mins} min`;
  }
  const secs = Math.round(ms / 1000);
  return `${secs} sec`;
}

export const LayerCard: React.FC<LayerCardProps> = ({
  layer,
  showOpacity = true,
  showInfo = true,
  showDragHandle = false,
  onDragStart,
  onDragEnd
}) => {
  const {
    toggleLayer,
    setLayerOpacity,
    getLayerInstance,
    selectionMode,
    selectedLayerIds,
    toggleLayerSelection
  } = useLayerDrawer();
  const { isAdmin, canDo } = usePermissions();
  const { openTool } = useTools();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const layerInstance = getLayerInstance(layer.id);
  const isEnabled = layerInstance?.isEnabled() || false;
  const currentOpacity = layerInstance?.getOpacity() || layer.default_opacity;
  const isSelected = selectedLayerIds.has(layer.id);
  const isBackgroundLayer = layer.category === 'backgrounds';
  const isLiveAffLayer = layer.type === 'live' && layer.config?.providerId === 'aff';

  // #866 — the AFF simulator's status, read only on the live AFF card, only
  // while the layer is on, and only for aff.read users (the route would refuse
  // anyone else).
  const canReadAff = canDo(AFF_FEATURE, AFF_READ_OP);
  const { status: simStatus, refresh: refreshSim } = useAffSimStatus(isLiveAffLayer && canReadAff && isEnabled);
  // A simulator start, stop or clear just took effect: redraw the aircraft now
  // rather than on the layer's next refresh.
  const simState = simStatus ? `${simStatus.startedAt}|${simStatus.simPositions}` : null;
  const lastSimState = useRef(simState);
  useEffect(() => {
    if (lastSimState.current !== null && simState !== null && simState !== lastSimState.current) {
      if (layerInstance instanceof LiveLayer) void layerInstance.refresh();
    }
    lastSimState.current = simState;
  }, [simState, layerInstance]);

  /**
   * Handle layer toggle
   */
  const handleToggle = useCallback(async () => {
    setIsToggling(true);
    setToggleError(null);
    try {
      await toggleLayer(layer.id);
    } catch (error) {
      console.error('Failed to toggle layer:', error);
      setToggleError(error instanceof Error ? error.message : 'Failed to toggle layer.');
    } finally {
      setIsToggling(false);
    }
  }, [layer.id, toggleLayer]);

  /**
   * Handle opacity change
   */
  const handleOpacityChange = useCallback(async (opacity: number) => {
    try {
      await setLayerOpacity(layer.id, opacity);
    } catch (error) {
      console.error('Failed to set opacity:', error);
    }
  }, [layer.id, setLayerOpacity]);

  /**
   * Toggle expanded state
   */
  const handleExpandToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  /**
   * Handle drag start
   */
  const handleDragStart = useCallback(() => {
    if (onDragStart) {
      onDragStart(layer.id);
    }
  }, [layer.id, onDragStart]);

  /**
   * Handle admin edit - opens layer admin with this layer
   */
  const handleAdminEdit = useCallback(() => {
    openTool('layerAdmin', { layerId: layer.id });
  }, [layer.id, openTool]);

  /**
   * Handle selection checkbox change
   */
  const handleSelectionChange = useCallback(() => {
    toggleLayerSelection(layer.id);
  }, [layer.id, toggleLayerSelection]);

  // #866 — the live AFF layer is aff.read; without it there is no toggle for a
  // layer whose feed would refuse the user. A drawn track's timeframe control
  // redraws track history, which is aff.explore.
  if (isLiveAffLayer && !canReadAff) return null;
  const canExploreAff = canDo(AFF_FEATURE, AFF_EXPLORE_OP);
  const canSimulateAff = canDo(AFF_FEATURE, AFF_SIMULATE_OP);

  return (
    <div
      className={`layer-card ${isEnabled ? 'layer-card--enabled' : ''} ${isExpanded ? 'layer-card--expanded' : ''} ${isSelected ? 'layer-card--selected' : ''} ${selectionMode ? 'layer-card--selection-mode' : ''}`}
      data-layer-id={layer.id}
    >
      <div className="layer-card-header">
        {/* Selection checkbox - shown in selection mode for non-background layers */}
        {selectionMode && !isBackgroundLayer && (
          <label className="layer-card-selection-checkbox">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={handleSelectionChange}
              aria-label={`Select ${layer.name}`}
            />
            <span className="checkbox-custom" />
          </label>
        )}

        {showDragHandle && !selectionMode && (
          <button
            className="layer-card-drag-handle"
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            aria-label={`Drag to reorder ${layer.name}`}
            title="Drag to reorder"
          >
            <span className="drag-handle-icon"><FontAwesomeIcon icon={faGripLines} /></span>
          </button>
        )}

        <div className="layer-card-main">
          <LayerIconPreview layer={layer} size={22} className="layer-card-icon" />

          <div className="layer-card-info">
            <div className="layer-card-name">{layer.name}</div>
            {layer.layer_group && (
              <div className="layer-card-group">{layer.layer_group}</div>
            )}
            {layer.config?.refreshInterval && (
              <div className="layer-card-refresh">
                Refreshes every {formatRefreshInterval(layer.config.refreshInterval)}
              </div>
            )}
          </div>
        </div>

        <div className="layer-card-actions">
          {showOpacity && isEnabled && (
            <button
              className={`layer-card-expand-btn ${isExpanded ? 'expanded' : ''}`}
              onClick={handleExpandToggle}
              aria-label={isExpanded ? 'Collapse controls' : 'Expand controls'}
              title={isExpanded ? 'Hide controls' : 'Show controls'}
            >
              <span className="expand-icon">▼</span>
            </button>
          )}

          {showInfo && (
            <button
              className="layer-card-info-btn"
              onClick={() => setShowInfoModal(true)}
              aria-label={`Info about ${layer.name}`}
              title="Layer information"
            >
              <span className="info-icon">ℹ</span>
            </button>
          )}

          {isAdmin && (
            <button
              className="layer-card-admin-btn"
              onClick={handleAdminEdit}
              aria-label={`Edit ${layer.name} in admin`}
              title="Edit layer"
            >
              <FontAwesomeIcon icon={faCog} />
            </button>
          )}

          <button
            className={`layer-card-toggle ${isEnabled ? 'enabled' : ''}`}
            onClick={handleToggle}
            disabled={isToggling}
            aria-label={isEnabled ? `Disable ${layer.name}` : `Enable ${layer.name}`}
            title={isEnabled ? 'Disable layer' : 'Enable layer'}
          >
            <span className="toggle-switch">
              {isToggling ? (
                <span className="loading-spinner"><FontAwesomeIcon icon={faSyncAlt} spin /></span>
              ) : (
                <span className="toggle-indicator" />
              )}
            </span>
          </button>
        </div>
      </div>

      {toggleError && (
        <div className="layer-card-error" role="alert">
          <span>{toggleError}</span>
          <button
            type="button"
            className="layer-card-error__refresh"
            onClick={() => window.location.reload()}
          >
            Refresh
          </button>
        </div>
      )}

      {/* Aircraft marked SIM on the layer are not real (#866). */}
      {isLiveAffLayer && isEnabled && <AffSimulatorBanner status={simStatus} />}

      {isExpanded && isEnabled && showOpacity && (
        <div className="layer-card-controls">
          <OpacitySlider
            layerId={layer.id}
            layerName={layer.name}
            opacity={currentOpacity}
            onChange={handleOpacityChange}
          />

          {/* A drawn AFF aircraft track carries its own timeframe (#802) — the
              same 24/48/72/Custom options as the map track duration, scoped to
              this one track. */}
          {layer.metadata?.aff && canExploreAff && <AffTrackTimeframeControl layer={layer} />}

          {/* How long an aircraft may go without reporting before it drops off
              the live map (#822). Keyed on providerId rather than the layer's
              name or id, so it survives the UUIDs migration 036 generates per
              environment — the same signal EasyMap and customLegends use.
              Deliberately NOT metadata.aff: that marks an ephemeral drawn
              track, and this belongs to the live aircraft layer alone. */}
          {isLiveAffLayer && <AffLiveWindowControl layer={layer} />}

          {/* The AFF simulator's Start, Stop and Clear (#866), aff.simulate only.
              They only record a request; the collector acts on its next run. */}
          {isLiveAffLayer && canSimulateAff && <AffSimulatorControls status={simStatus} onRequested={refreshSim} />}

          {/* Metadata section - shows attribution, description, refresh interval */}
          {(layer.metadata?.attribution || layer.metadata?.description || layer.config?.refreshInterval || layer.source_url) && (
            <div className="layer-card-metadata">
              {layer.metadata?.description && (
                <div className="layer-card-metadata-item">
                  <span className="layer-card-metadata-label">Description:</span>
                  <span className="layer-card-metadata-value">{layer.metadata.description}</span>
                </div>
              )}
              {layer.metadata?.attribution && (
                <div className="layer-card-metadata-item">
                  <span className="layer-card-metadata-label">Attribution:</span>
                  <span className="layer-card-metadata-value">{layer.metadata.attribution}</span>
                </div>
              )}
              {layer.config?.refreshInterval && (
                <div className="layer-card-metadata-item">
                  <span className="layer-card-metadata-label">Refresh:</span>
                  <span className="layer-card-metadata-value">
                    {layer.config.refreshInterval >= 60000
                      ? `${Math.round(layer.config.refreshInterval / 60000)} min`
                      : `${Math.round(layer.config.refreshInterval / 1000)} sec`}
                  </span>
                </div>
              )}
              {layer.source_url && (
                <div className="layer-card-metadata-item">
                  <span className="layer-card-metadata-label">Source:</span>
                  <a
                    href={layer.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="layer-card-metadata-link"
                    title={layer.source_url}
                  >
                    View Source
                  </a>
                </div>
              )}
            </div>
          )}

          {layer.legend_url && (
            <div className="layer-card-legend">
              <div className="layer-card-legend-title">Legend</div>
              <img
                src={layer.legend_url}
                alt={`Legend for ${layer.name}`}
                className="layer-card-legend-image"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>
      )}

      {showInfoModal && (
        <AppModal
          isOpen
          onClose={() => setShowInfoModal(false)}
          width={700}
          maxHeight="90vh"
          header={<h3>{layer.name}</h3>}
        >
          <div className="layer-info-modal">
            <div className="layer-info-modal-body">
              <div className="layer-info-section">
                <h4 className="layer-info-section-title">Basic Information</h4>
                <div className="layer-info-row">
                  <span className="layer-info-label">Type:</span>
                  <span className="layer-info-value">{layer.type.toUpperCase()}</span>
                </div>
                <div className="layer-info-row">
                  <span className="layer-info-label">Category:</span>
                  <span className="layer-info-value">{layer.category}</span>
                </div>
                {layer.layer_group && (
                  <div className="layer-info-row">
                    <span className="layer-info-label">Group:</span>
                    <span className="layer-info-value">{layer.layer_group}</span>
                  </div>
                )}
                <div className="layer-info-row">
                  <span className="layer-info-label">Zoom Range:</span>
                  <span className="layer-info-value">{layer.zoom_min} - {layer.zoom_max}</span>
                </div>
                {layer.tags.length > 0 && (
                  <div className="layer-info-row">
                    <span className="layer-info-label">Tags:</span>
                    <span className="layer-info-value">{layer.tags.join(', ')}</span>
                  </div>
                )}
              </div>

              <div className="layer-info-section">
                <h4 className="layer-info-section-title">Source</h4>
                {layer.source_url && (
                  <div className="layer-info-row">
                    <span className="layer-info-label">Source URL:</span>
                    <span className="layer-info-value layer-info-url">{layer.source_url}</span>
                  </div>
                )}
                {layer.legend_url && (
                  <div className="layer-info-row">
                    <span className="layer-info-label">Legend:</span>
                    <img
                      src={layer.legend_url}
                      alt={`Legend for ${layer.name}`}
                      className="layer-card-legend-image"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              {layer.config && Object.keys(layer.config).length > 0 && (
                <div className="layer-info-section">
                  <h4 className="layer-info-section-title">Configuration</h4>
                  <pre className="layer-info-code-block">
                    {JSON.stringify(layer.config, null, 2)}
                  </pre>
                </div>
              )}

              {layer.metadata && Object.keys(layer.metadata).length > 0 && (
                <div className="layer-info-section">
                  <h4 className="layer-info-section-title">Metadata</h4>
                  {layer.metadata.description && (
                    <div className="layer-info-row">
                      <span className="layer-info-label">Description:</span>
                      <span className="layer-info-value">{layer.metadata.description}</span>
                    </div>
                  )}
                  {layer.metadata.attribution && (
                    <div className="layer-info-row">
                      <span className="layer-info-label">Attribution:</span>
                      <span className="layer-info-value">{layer.metadata.attribution}</span>
                    </div>
                  )}
                  {layer.metadata.machineId && (
                    <div className="layer-info-row">
                      <span className="layer-info-label">Machine ID:</span>
                      <span className="layer-info-value layer-info-monospace">{layer.metadata.machineId}</span>
                    </div>
                  )}
                  {layer.config?.refreshInterval && (
                    <div className="layer-info-row">
                      <span className="layer-info-label">Refresh Interval:</span>
                      <span className="layer-info-value">
                        {layer.config.refreshInterval >= 60000
                          ? `${Math.round(layer.config.refreshInterval / 60000)} minutes`
                          : `${Math.round(layer.config.refreshInterval / 1000)} seconds`}
                      </span>
                    </div>
                  )}
                  {layer.metadata.getFeatureInfo !== undefined && (
                    <div className="layer-info-row">
                      <span className="layer-info-label">Get Feature Info:</span>
                      <span className="layer-info-value">{layer.metadata.getFeatureInfo ? 'Enabled' : 'Disabled'}</span>
                    </div>
                  )}
                  {/* Show remaining metadata as JSON if there are other fields */}
                  {Object.keys(layer.metadata).filter(k => !['description', 'attribution', 'machineId', 'getFeatureInfo'].includes(k)).length > 0 && (
                    <details className="layer-info-details">
                      <summary>Additional Metadata</summary>
                      <pre className="layer-info-code-block">
                        {JSON.stringify(
                          Object.fromEntries(
                            Object.entries(layer.metadata).filter(
                              ([k]) => !['description', 'attribution', 'machineId', 'getFeatureInfo'].includes(k)
                            )
                          ),
                          null,
                          2
                        )}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          </div>
        </AppModal>
      )}
    </div>
  );
};
